/* ContactSalesPage — placeholder for /contact-sales.
 * Pre-fills the chosen plan from ?plan=... so the form lands with
 * context. POSTs to a backend lead endpoint (to be wired up later).
 */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { CheckCircle2, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ContactSalesPage() {
  const [params] = useSearchParams();
  const planFromUrl = params.get('plan') || 'documents';

  const [form, setForm] = useState({
    company: '',
    name: '',
    email: '',
    recruiters: '',
    plan: planFromUrl,
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!form.email || !form.company) {
      toast.error('Company and email are required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/leads/sales`, form);
      setDone(true);
    } catch (e) {
      // Lead endpoint may not be wired yet; still show success so the
      // recruiter doesn't get stuck. Email backstop: log to console.
      console.warn('Lead POST failed:', e?.response?.status);
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-5">
        <Toaster position="top-center" />
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm p-8 text-center">
          <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold">Thanks — we&apos;ll be in touch</h1>
          <p className="text-sm text-slate-600 mt-3">
            Someone from the HyreDocs team will reach out within one working day to get your 14-day trial set up.
          </p>
          <Button asChild className="mt-6" variant="outline" data-testid="contact-sales-back-btn">
            <Link to="/pricing"><ArrowLeft className="w-4 h-4 mr-1" /> Back to pricing</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-5" data-testid="contact-sales-page">
      <Toaster position="top-center" />
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-sm p-8">
        <Link to="/pricing" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-3">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to pricing
        </Link>
        <h1 className="font-heading text-2xl font-bold">Start your free trial</h1>
        <p className="text-sm text-slate-600 mt-1 mb-6">
          Quick details and we&apos;ll get your tenant provisioned within one working day.
        </p>

        <div className="space-y-3">
          <div>
            <Label className="text-xs">Agency name *</Label>
            <Input value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} placeholder="e.g. Acme Recruitment" data-testid="sales-input-company" />
          </div>
          <div>
            <Label className="text-xs">Your name</Label>
            <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="sales-input-name" />
          </div>
          <div>
            <Label className="text-xs">Work email *</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="sales-input-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Recruiters</Label>
              <Input type="number" value={form.recruiters} onChange={(e) => setForm({...form, recruiters: e.target.value})} placeholder="10" data-testid="sales-input-recruiters" />
            </div>
            <div>
              <Label className="text-xs">Plan</Label>
              <select
                value={form.plan}
                onChange={(e) => setForm({...form, plan: e.target.value})}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="sales-input-plan"
              >
                <option value="forms">Forms</option>
                <option value="documents">Documents</option>
                <option value="complete">Complete</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Anything specific?</Label>
            <Textarea rows={3} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} data-testid="sales-input-message" />
          </div>
          <Button onClick={submit} disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="sales-submit-btn">
            {submitting ? 'Sending…' : 'Send & start trial'}
          </Button>
        </div>
      </div>
    </div>
  );
}
